import { useQuery } from '@tanstack/react-query';
import {
  MOTIVO_REJEICAO_LABEL,
  MOTIVOS_REJEICAO_INDICACAO,
  type MetricasResposta,
} from '@varga/shared';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { obterMetricas, type PeriodoMetricas } from '../api/metricas';
import { CabecalhoTela } from '../components/Shell';
import { BlocoAviso, LinhasEsqueleto } from '../components/ui';
import { useAuth } from '../lib/auth';

const PERIODOS: { valor: PeriodoMetricas; texto: string }[] = [
  { valor: 'mes', texto: 'Mês atual' },
  { valor: '30d', texto: 'Últimos 30 dias' },
  { valor: 'campanha', texto: 'Campanha inteira' },
];

export function DashboardPagina(): ReactNode {
  const [periodo, setPeriodo] = useState<PeriodoMetricas>('mes');
  const { usuario } = useAuth();
  const q = useQuery({
    queryKey: ['metricas', periodo],
    queryFn: () => obterMetricas(periodo),
  });

  return (
    <div>
      <CabecalhoTela
        titulo="Dashboard"
        acoes={
          <div className="flex gap-1 rounded-1 border border-linha-forte p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p.valor}
                type="button"
                aria-pressed={periodo === p.valor}
                onClick={() => setPeriodo(p.valor)}
                className={
                  'rounded-1 px-2.5 py-1 text-nota ' +
                  (periodo === p.valor ? 'bg-acento-fraco text-tinta-forte' : 'text-tinta-2')
                }
              >
                {p.texto}
              </button>
            ))}
          </div>
        }
      />

      {q.isLoading && <LinhasEsqueleto linhas={4} colunas={3} />}
      {q.isError && (
        <BlocoAviso tom="aviso" aoTentarDeNovo={() => void q.refetch()}>
          Não foi possível carregar as métricas.
        </BlocoAviso>
      )}
      {q.data && <Grade m={q.data} ehAdmin={usuario?.role === 'admin'} />}
    </div>
  );
}

function Grade({ m, ehAdmin }: { m: MetricasResposta; ehAdmin: boolean }): ReactNode {
  const semDados = m.indicacoesConvertidas === 0 && m.indicacoesRejeitadas === 0;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Bloco
        titulo="Indicações convertidas"
        valor={String(m.indicacoesConvertidas)}
        leitura="Serviços fechados com um código de indicação no período."
      />
      <Bloco
        titulo="Taxa de conversão"
        valor={`${Math.round(m.taxaConversao * 100)}%`}
        leitura="Convertidas sobre o total de indicações lançadas."
      />
      <Bloco
        titulo="Brindes concedidos × entregues"
        valor={`${m.brindesConcedidos} / ${m.brindesEntregues}`}
        leitura={`Taxa de retirada: ${Math.round(m.taxaRetirada * 100)}%.`}
      />
      <Bloco
        titulo="Vouchers parados"
        valor={String(m.vouchersParados)}
        leitura="Disponíveis há mais de 30 dias — proxy de falha de comunicação."
      />
      <Bloco
        titulo="Custo estimado concedido"
        valor={m.custoConcedido == null ? 'indisponível' : formatarReais(m.custoConcedido)}
        leitura={
          m.custoConcedido == null ? (
            <>
              Preencha o custo dos brindes em Campanha para ver o custo concedido.{' '}
              {ehAdmin && (
                <Link to="/campanha" className="text-acento-texto underline">
                  Ir para Campanha
                </Link>
              )}
            </>
          ) : m.custoConcedidoParcial ? (
            'Parte das concessões ainda não tem custo lançado.'
          ) : (
            'Soma do custo estimado das concessões no período.'
          )
        }
      />
      <Bloco
        titulo="K-factor"
        valor={m.kFactor == null ? '—' : m.kFactor.toFixed(2)}
        leitura="Aproximação — parte dos indicados viria sem o programa; ver definição em O4."
      />

      <div className="rounded-2 border border-linha bg-superficie p-4 md:col-span-2">
        <h3 className="font-display text-rotulo uppercase text-tinta-2">Rejeições por motivo</h3>
        {semDados ? (
          <p className="mt-2 text-corpo text-tinta-2">Nenhuma indicação neste período.</p>
        ) : (
          <BarraRejeicoes dados={m.rejeicoesPorMotivo} />
        )}
        <p className="mt-2 text-nota text-tinta-2">
          Muitos «código não existe» costuma ser cartão ou mensagem de indicação confusos.
        </p>
      </div>

      <div className="rounded-2 border border-linha bg-superficie p-4">
        <h3 className="font-display text-rotulo uppercase text-tinta-2">Ranking de indicadores</h3>
        {m.rankingIndicadores.length === 0 ? (
          <p className="mt-2 text-corpo text-tinta-2">Sem indicadores no período.</p>
        ) : (
          <ol className="mt-2 flex flex-col gap-1 text-corpo">
            {m.rankingIndicadores.map((r) => (
              <li key={r.clienteId} className="flex justify-between">
                <Link to={`/clientes/${r.clienteId}`} className="text-acento-texto underline">
                  {r.primeiroNome}
                </Link>
                <span className="tnum text-tinta-forte">{r.conversoes}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function Bloco({
  titulo,
  valor,
  leitura,
}: {
  titulo: string;
  valor: string;
  leitura: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-2 border border-linha bg-superficie p-4">
      <h3 className="font-display text-rotulo uppercase text-tinta-2">{titulo}</h3>
      <p className="tnum mt-1 font-display text-numero-kpi font-bold text-tinta-forte">{valor}</p>
      <p className="mt-1 text-nota text-tinta-2">{leitura}</p>
    </div>
  );
}

function BarraRejeicoes({ dados }: { dados: Record<string, number> }): ReactNode {
  const max = Math.max(1, ...Object.values(dados));
  return (
    <div className="mt-3 flex flex-col gap-1.5">
      {MOTIVOS_REJEICAO_INDICACAO.map((motivo) => {
        const n = dados[motivo] ?? 0;
        return (
          <div key={motivo} className="flex items-center gap-2 text-nota">
            <span className="w-56 shrink-0 text-tinta-2">{MOTIVO_REJEICAO_LABEL[motivo]}</span>
            <div className="h-3 flex-1 rounded-1 bg-poco">
              <div className="h-3 rounded-1 bg-acento" style={{ width: `${(n / max) * 100}%` }} />
            </div>
            <span className="tnum w-6 text-right text-tinta-forte">{n}</span>
          </div>
        );
      })}
    </div>
  );
}

function formatarReais(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
